import axios from 'axios'
import { getAiToolPdfApiConfig } from './apiConfig'
import { AI_TOOL_TYPES } from './constants'

const SAMPLE_TOOL_TYPES = new Set(Object.values(AI_TOOL_TYPES))

function parseApiError(data) {
  if (typeof data !== 'string') return null
  try {
    const json = JSON.parse(data)
    return json?.message || null
  } catch {
    return null
  }
}

export async function downloadAiToolPdf(
  aiuId,
  filename = 'report',
  { exportId = 'ai-tool-pdf-export' } = {},
) {
  if (!aiuId) {
    throw new Error('PDF is not available. Please run a new analysis.')
  }

  let htmlContent
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/ai-tool/pdf/${aiuId}`,
      getAiToolPdfApiConfig(),
    )
    htmlContent = response?.data
  } catch (err) {
    const message = parseApiError(err?.response?.data)
    throw new Error(message || err?.message || 'Failed to download PDF.')
  }

  if (typeof htmlContent !== 'string' || !htmlContent.trim()) {
    throw new Error('PDF content is not available.')
  }

  const mod = await import('../productionTools/pdfExportCore')
  const { parsePdfHtmlContent, renderHtmlToPdf } = mod

  if (
    typeof parsePdfHtmlContent !== 'function' ||
    typeof renderHtmlToPdf !== 'function'
  ) {
    throw new Error(
      'PDF export module unavailable. Please refresh the page and try again.',
    )
  }

  const parsed = parsePdfHtmlContent(htmlContent)
  if (!parsed) {
    throw new Error('PDF content is not available.')
  }

  await renderHtmlToPdf({
    html: parsed.bodyHtml,
    styles: parsed.styles,
    backgroundColor: parsed.backgroundColor,
    title: filename,
    exportId,
  })
}

export async function downloadSampleAiToolPdf(
  type,
  filename = 'sample-report',
  { exportId = 'ai-tool-sample-pdf-export' } = {},
) {
  if (!SAMPLE_TOOL_TYPES.has(type)) {
    throw new Error('Invalid sample PDF type.')
  }

  let htmlContent
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/ai-tool/pdf/sample/${type}`,
      getAiToolPdfApiConfig(),
    )
    htmlContent = response?.data
  } catch (err) {
    const message = parseApiError(err?.response?.data)
    throw new Error(message || err?.message || 'Failed to download sample PDF.')
  }

  if (typeof htmlContent !== 'string' || !htmlContent.trim()) {
    throw new Error('Sample PDF content is not available.')
  }

  const mod = await import('../productionTools/pdfExportCore')
  const { parsePdfHtmlContent, renderHtmlToPdf } = mod

  if (
    typeof parsePdfHtmlContent !== 'function' ||
    typeof renderHtmlToPdf !== 'function'
  ) {
    throw new Error(
      'PDF export module unavailable. Please refresh the page and try again.',
    )
  }

  const parsed = parsePdfHtmlContent(htmlContent)
  if (!parsed) {
    throw new Error('Sample PDF content is not available.')
  }

  await renderHtmlToPdf({
    html: parsed.bodyHtml,
    styles: parsed.styles,
    backgroundColor: parsed.backgroundColor,
    title: filename,
    exportId,
  })
}
